import React from 'react';
import { User } from '../services/authService.ts';
import { Crown, Briefcase, Star, User as UserIcon } from 'lucide-react';

// ─── Paleta Oficial Co Marca (Ishtar Villazón & Carlos) ───
export const COMARCA_COLORS = {
    moradoPrincipal: '#61257B',
    moradoVariante: '#C1348B',
    naranjaIntenso: '#EA8406',
    naranjaVariante: '#FFD97C',
    primary: '#c1348b',       // Rosa neón
    primaryDark: '#8f1f64',   // Vino oscuro
    secondary: '#61257b',     // Azul
    secondaryLight: '#8b3fa8',// Turquesa
    bgLight: '#F8FAFC',
};

export type UserRingTier = 'superadmin' | 'personal' | 'domiciliado' | 'comun';

export interface TierConfig {
    tier: UserRingTier;
    label: string;
    sublabel: string;
    ringBackground: string;
    ringBorder: string;
    boxShadow: string;
    badgeBg: string;
    badgeBorder: string;
    badgeColor: string;
    iconText: React.ReactNode;
    glowColor: string;
}

// ─── 4 Tiers Exclusivos según directiva ───
export const TIER_CONFIGS: Record<UserRingTier, TierConfig> = {
    // 1. Super Admin: El ÚNICO con la Estela arcoíris Google Co Marca completa
    superadmin: {
        tier: 'superadmin',
        label: 'Super Admin',
        sublabel: 'Administración Global',
        ringBackground: 'conic-gradient(from 180deg, #61257B 0%, #C1348B 16%, #c1348b 32%, #EA8406 48%, #FFD97C 64%, #8b3fa8 80%, #61257b 92%, #61257B 100%)',
        ringBorder: 'none',
        boxShadow: '0 0 16px rgba(97, 37, 123, 0.45), 0 0 10px rgba(97, 37, 123, 0.35)',
        badgeBg: 'rgba(97, 37, 123, 0.12)',
        badgeBorder: '1.5px solid rgba(97, 37, 123, 0.3)',
        badgeColor: '#61257B',
        iconText: <Crown size={12} />,
        glowColor: 'rgba(97, 37, 123, 0.45)',
    },

    // 2. Personal / Staff: Aro diferente Corporativo Azul (#61257b) a Turquesa (#8b3fa8)
    personal: {
        tier: 'personal',
        label: 'Personal Co Marca',
        sublabel: 'Equipo Oficial',
        ringBackground: 'linear-gradient(135deg, #61257b 0%, #8b3fa8 100%)',
        ringBorder: 'none',
        boxShadow: '0 0 14px rgba(97, 37, 123, 0.4), 0 0 6px rgba(139, 63, 168, 0.3)',
        badgeBg: 'rgba(97, 37, 123, 0.12)',
        badgeBorder: '1.5px solid rgba(97, 37, 123, 0.3)',
        badgeColor: '#61257b',
        iconText: <Briefcase size={12} />,
        glowColor: 'rgba(97, 37, 123, 0.35)',
    },

    // 3. Domiciliados o Suscriptores Mensual / Anual: Aro VIP Naranja Intenso (#EA8406) a Dorado (#FFD97C) y Rosa (#c1348b)
    domiciliado: {
        tier: 'domiciliado',
        label: 'Domiciliado / Suscriptor',
        sublabel: 'Plan Mensual / Anual Activo',
        ringBackground: 'linear-gradient(135deg, #EA8406 0%, #FFD97C 48%, #c1348b 100%)',
        ringBorder: 'none',
        boxShadow: '0 0 14px rgba(234, 132, 6, 0.4), 0 0 6px rgba(193, 52, 139, 0.3)',
        badgeBg: 'rgba(234, 132, 6, 0.12)',
        badgeBorder: '1.5px solid rgba(234, 132, 6, 0.3)',
        badgeColor: '#EA8406',
        iconText: <Star size={12} />,
        glowColor: 'rgba(234, 132, 6, 0.35)',
    },

    // 4. Usuario Común (Recurrente o Eventual): Aro común y limpio, sin degradado multicolor
    comun: {
        tier: 'comun',
        label: 'Miembro Co Marca',
        sublabel: 'Usuario Recurrente / Eventual',
        ringBackground: '#e2e8f0',
        ringBorder: '1.5px solid #cbd5e1',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
        badgeBg: '#f1f5f9',
        badgeBorder: '1px solid #e2e8f0',
        badgeColor: '#64748b',
        iconText: <UserIcon size={12} />,
        glowColor: 'rgba(0, 0, 0, 0.05)',
    },
};

/**
 * Comprueba si un usuario tiene rol de Super Administrador.
 */
export const isUserSuperAdmin = (user: User | null | undefined): boolean => {
    if (!user) return false;
    const role = (user.role || '').toLowerCase().trim();
    return (
        role === 'superadmin' ||
        role === 'admin' ||
        role === 'administrador' ||
        user.user_level_id === 1
    );
};

export const getDynamicTierLabel = (user: User | null | undefined, tierConfig: TierConfig): string => {
    if (tierConfig.tier === 'domiciliado') {
        if (user && user.role) {
            return user.role.charAt(0).toUpperCase() + user.role.slice(1);
        }
    }
    return tierConfig.label;
};

export const getDynamicTierIcon = (tierConfig: TierConfig): React.ReactNode | null => {
    if (tierConfig.tier === 'domiciliado') {
        return null;
    }
    return tierConfig.iconText;
};

export const getDynamicTierSublabel = (user: User | null | undefined, tierConfig: TierConfig): string => {
    if (tierConfig.tier === 'domiciliado') {
        if (user && user.role) {
            return 'Nivel de Usuario Actual';
        }
    }
    return tierConfig.sublabel;
};

/**
 * Determina el tier de aro correspondiente para un usuario según sus roles y suscripciones.
 * Es 100% automático según el plan adquirido. Solo el Super Admin puede utilizar el simulador de prueba.
 */
export const getUserRingTier = (user: User | null | undefined): UserRingTier => {
    if (!user) return 'comun';

    // 1. Super Admin: El ÚNICO que lleva la Estela Arcoíris Google Co Marca
    if (isUserSuperAdmin(user)) {
        // Si el Super Admin está probando con el simulador:
        const simTier = localStorage.getItem('user_tier_simulator') as UserRingTier | null;
        if (simTier && TIER_CONFIGS[simTier]) {
            return simTier;
        }
        return 'superadmin';
    }

    // Para cualquier otro usuario, se asigna AUTOMÁTICAMENTE según su plan y perfil
    // (Limpiamos cualquier simulador previo por seguridad)
    if (localStorage.getItem('user_tier_simulator')) {
        localStorage.removeItem('user_tier_simulator');
    }

    // 2. Personal / Staff: Perfil personal o personal_id asignado o rol de staff (UserLevel 2)
    const role = (user.role || '').toLowerCase().trim();
    if (
        user.perfil === 'personal' ||
        !!user.personal_id ||
        role === 'personal' ||
        role === 'staff' ||
        user.user_level_id === 2
    ) {
        return 'personal';
    }

    // 3. Domiciliado o Suscripción mensual / anual / Afiliado (UserLevels 5 y 6)
    const planStorage = (localStorage.getItem('user_plan_tipo') || '').toLowerCase();
    const subStorage = (localStorage.getItem('user_suscripcion') || '').toLowerCase();
    const esDomiciliadoOSuscrito = 
        user.es_domiciliado === true || 
        (user as any).es_afiliado === true ||
        !!user.domiciliacion_empresa || 
        user.tiene_servicio_correspondencia === true || 
        user.tiene_correspondencia_habilitada === true ||
        role.includes('domicili') ||
        role.includes('afiliad') ||
        user.user_level_id === 5 || // Afiliado
        user.user_level_id === 6 || // Domiciliado
        planStorage.includes('mensual') ||
        planStorage.includes('anual') ||
        planStorage.includes('afiliad') ||
        planStorage.includes('domicili') ||
        subStorage.includes('mensual') ||
        subStorage.includes('anual');

    if (esDomiciliadoOSuscrito) {
        return 'domiciliado';
    }

    // 4. Usuario Común (recurrente o eventual):
    return 'comun';
};

/**
 * Determina el color de la profesión según el cargo / especialidad / rama del usuario.
 */
export const getProfessionColor = (userData: any): { color: string; colorDark: string; label: string; categoria: string } => {
    const userId = userData?.id;
    const savedCat = (
        (userId ? localStorage.getItem(`user_categoria_${userId}`) : null) ||
        localStorage.getItem('user_categoria') ||
        userData?.categoria_profesion ||
        userData?.rama_codigo ||
        ''
    ).toLowerCase().trim();

    const savedProf = (
        (userId ? localStorage.getItem(`user_profesion_${userId}`) : null) ||
        localStorage.getItem('user_profesion') ||
        ''
    ).trim();

    // 1. Revisar caché dinámico de Ramas Profesionales
    try {
        const cachedRamas = localStorage.getItem('comarca_ramas_cache');
        if (cachedRamas) {
            const parsed = JSON.parse(cachedRamas);
            if (Array.isArray(parsed)) {
                const match = parsed.find((r: any) =>
                    r.codigo?.toLowerCase() === savedCat ||
                    String(r.id) === savedCat ||
                    r.nombre?.toLowerCase() === savedCat
                );
                if (match) {
                    return {
                        color: match.color || COMARCA_COLORS.moradoPrincipal,
                        colorDark: match.color_dark || COMARCA_COLORS.moradoVariante,
                        label: savedProf || userData?.cargo || match.nombre,
                        categoria: match.codigo || savedCat,
                    };
                }
            }
        }
    } catch (e) {}

    // 2. Si hay una categoría explícitamente guardada:
    if (savedCat === 'ingenieria' || savedCat === 'arquitectura') {
        return { color: COMARCA_COLORS.moradoPrincipal, colorDark: COMARCA_COLORS.moradoVariante, label: savedProf || userData?.cargo || 'Ingeniería & Arquitectura', categoria: 'ingenieria' };
    }
    if (savedCat === 'finanzas' || savedCat === 'contabilidad') {
        return { color: COMARCA_COLORS.naranjaIntenso, colorDark: '#b35900', label: savedProf || userData?.cargo || 'Finanzas & Contabilidad', categoria: 'finanzas' };
    }
    if (savedCat === 'marketing' || savedCat === 'comunicacion') {
        return { color: COMARCA_COLORS.primary, colorDark: COMARCA_COLORS.moradoVariante, label: savedProf || userData?.cargo || 'Marketing & Comunicación', categoria: 'marketing' };
    }
    if (savedCat === 'tecnologia' || savedCat === 'software') {
        return { color: COMARCA_COLORS.secondary, colorDark: '#004d7a', label: savedProf || userData?.cargo || 'Tecnología & Software', categoria: 'tecnologia' };
    }
    if (savedCat === 'salud' || savedCat === 'bienestar') {
        return { color: COMARCA_COLORS.secondaryLight, colorDark: '#006b5a', label: savedProf || userData?.cargo || 'Salud & Bienestar', categoria: 'salud' };
    }
    if (savedCat === 'derecho' || savedCat === 'legal') {
        return { color: COMARCA_COLORS.primaryDark, colorDark: '#60002e', label: savedProf || userData?.cargo || 'Derecho & Legal', categoria: 'derecho' };
    }

    // 3. Evaluar cadenas de cargo / profesión / área / rol
    const raw = (
        savedProf ||
        userData?.cargo || 
        userData?.profesion || 
        userData?.area || 
        userData?.rubro || 
        userData?.role || 
        userData?.email || 
        ''
    ).toLowerCase().trim();

    // Tecnología & Software
    const isTech = 
        raw.includes('tech') || 
        raw.includes('soft') || 
        raw.includes('dev') || 
        raw.includes('program') || 
        raw.includes('sistemas') || 
        raw.includes('frontend') || 
        raw.includes('backend') || 
        raw.includes('cloud') || 
        raw.includes('data') ||
        raw.includes('stack') ||
        raw.includes('informát');

    if (isTech) {
        return { color: COMARCA_COLORS.secondary, colorDark: '#004d7a', label: userData?.cargo || 'Tecnología & Software', categoria: 'tecnologia' };
    }

    // Ingeniería & Arquitectura
    if (
        raw.includes('civil') || 
        raw.includes('arq') || 
        raw.includes('industrial') || 
        raw.includes('construct') || 
        raw.includes('obra') ||
        raw.includes('mecánic') ||
        raw.includes('eléctric') ||
        raw.includes('ingenier') ||
        raw.includes('ing.')
    ) {
        return { color: COMARCA_COLORS.moradoPrincipal, colorDark: COMARCA_COLORS.moradoVariante, label: userData?.cargo || 'Ingeniería & Arquitectura', categoria: 'ingenieria' };
    }

    // Derecho & Legal
    if (
        raw.includes('abog') || 
        raw.includes('legal') || 
        raw.includes('derecho') || 
        raw.includes('jurid') || 
        raw.includes('fiscal') || 
        raw.includes('notar') ||
        raw.includes('compliance') ||
        raw.includes('ley')
    ) {
        return { color: COMARCA_COLORS.primaryDark, colorDark: '#60002e', label: userData?.cargo || 'Derecho & Legal', categoria: 'derecho' };
    }

    // Finanzas & Contabilidad
    if (
        raw.includes('contad') || 
        raw.includes('finan') || 
        raw.includes('audit') || 
        raw.includes('econom') || 
        raw.includes('banc') || 
        raw.includes('tribut') ||
        raw.includes('niif') ||
        raw.includes('balance')
    ) {
        return { color: COMARCA_COLORS.naranjaIntenso, colorDark: '#b35900', label: userData?.cargo || 'Finanzas & Contabilidad', categoria: 'finanzas' };
    }

    // Salud & Bienestar
    if (
        raw.includes('salud') || 
        raw.includes('medic') || 
        raw.includes('psicol') || 
        raw.includes('nutri') || 
        raw.includes('bienestar') || 
        raw.includes('coach') ||
        raw.includes('terap') ||
        raw.includes('clínic')
    ) {
        return { color: COMARCA_COLORS.secondaryLight, colorDark: '#006b5a', label: userData?.cargo || 'Salud & Bienestar', categoria: 'salud' };
    }

    // Marketing & Comunicación
    if (
        raw.includes('market') || 
        raw.includes('comunic') || 
        raw.includes('publicid') || 
        raw.includes('diseñ') || 
        raw.includes('design') || 
        raw.includes('creativ') || 
        raw.includes('social') || 
        raw.includes('content') ||
        raw.includes('comercial') ||
        raw.includes('ventas') ||
        raw.includes('pyme') ||
        raw.includes('consultor') ||
        raw.includes('brand')
    ) {
        return { color: COMARCA_COLORS.primary, colorDark: COMARCA_COLORS.moradoVariante, label: userData?.cargo || 'Marketing & Comunicación', categoria: 'marketing' };
    }

    return { color: COMARCA_COLORS.moradoPrincipal, colorDark: COMARCA_COLORS.moradoVariante, label: userData?.cargo || 'Miembro', categoria: 'ingenieria' };
};

/**
 * Componente unificado para el Avatar con Aro diferenciado según el Tier y Rama Profesional del Usuario
 */
export interface TierAvatarProps {
    tier?: UserRingTier;
    user?: User | null;
    size?: number;
    initial?: string;
    bgColor?: string;
    fotoUrl?: string | null;
    isOpenOrHovered?: boolean;
    ringThickness?: number;
}

export const TierAvatar: React.FC<TierAvatarProps> = ({
    tier,
    user,
    size = 40,
    initial = 'C',
    bgColor,
    fotoUrl = null,
    isOpenOrHovered = false,
    ringThickness = 2.5,
}) => {
    const activeTier = tier || getUserRingTier(user);
    const profColor = getProfessionColor(user);
    const finalBgColor = bgColor || profColor.color;

    // Si el usuario es de nivel 'comun', el aro exterior resalta directamente su rama profesional:
    const ringBackground = activeTier === 'comun'
        ? `linear-gradient(135deg, ${profColor.color} 0%, ${profColor.colorDark} 100%)`
        : (TIER_CONFIGS[activeTier] || TIER_CONFIGS.comun).ringBackground;

    const ringBorder = activeTier === 'comun'
        ? 'none'
        : (TIER_CONFIGS[activeTier] || TIER_CONFIGS.comun).ringBorder;

    // Sombra según estado y color de rama
    const currentShadow = isOpenOrHovered
        ? `0 0 16px ${profColor.color}70, 0 4px 12px rgba(0,0,0,0.22)`
        : (activeTier === 'comun'
            ? `0 0 10px ${profColor.color}45, 0 2px 6px rgba(0,0,0,0.1)`
            : (TIER_CONFIGS[activeTier] || TIER_CONFIGS.comun).boxShadow);

    return (
        <div
            style={{
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: '50%',
                background: ringBackground,
                border: ringBorder !== 'none' ? ringBorder : undefined,
                padding: `${ringThickness}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: currentShadow,
                transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
                transform: isOpenOrHovered ? 'scale(1.05)' : 'scale(1)',
                boxSizing: 'border-box',
                flexShrink: 0,
                position: 'relative',
            }}
        >
            {/* Anillo espaciador blanco interior */}
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: '#ffffff',
                    padding: `${ringThickness > 2 ? 2 : 1.5}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                }}
            >
                {/* Núcleo interior: Color de profesión del usuario con foto o inicial */}
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: finalBgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                >
                    {fotoUrl ? (
                        <img
                            src={fotoUrl}
                            alt="avatar"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                        />
                    ) : (
                        <span
                            style={{
                                color: '#ffffff',
                                fontFamily: "'DM Sans', sans-serif",
                                fontWeight: '800',
                                fontSize: `${size * 0.4}px`,
                                lineHeight: 1,
                                textTransform: 'uppercase',
                                userSelect: 'none',
                            }}
                        >
                            {initial}
                        </span>
                    )}
                </div>
            </div>

            {/* Píldora indicadora de la Rama Profesional en la esquina inferior */}
            <div
                title={`Rama: ${profColor.label}`}
                style={{
                    position: 'absolute',
                    bottom: '-1px',
                    right: '-1px',
                    width: `${Math.max(10, Math.round(size * 0.28))}px`,
                    height: `${Math.max(10, Math.round(size * 0.28))}px`,
                    borderRadius: '50%',
                    background: profColor.color,
                    border: '1.5px solid #ffffff',
                    boxShadow: `0 0 6px ${profColor.color}90`,
                    zIndex: 6,
                    pointerEvents: 'none',
                }}
            />
        </div>
    );
};
